import React, { FC, ReactElement } from 'react';
import { observer } from 'mobx-react-lite';
import { Input } from 'src/domains/common/components/input/input';
import { Select } from 'src/domains/common/components/select/select';
import { useCommon } from 'src/AppState/Common';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { MultiOptionSelect } from 'src/domains/common/components/multi-option-select/multi-option-select';
import { SportsPicker } from './sports-picker/SportsPicker';
import { formatNumericInput } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { ResultsMultiSelect } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/ResultsMultiSelect';
import { BET_TYPE_OPTIONS } from 'src/domains/casino/webview/bonus-engine/common/const';

type SportsbookSectionsPropsType = {
    campaignId?: string;
};

export const SportsbookSections: FC<SportsbookSectionsPropsType> = observer(({ campaignId }): ReactElement => {
    const common = useCommon();
    const { generalDetailsState, actionConditionsState, editionDisabled } = CampaignCreatorState.get(
        common,
        campaignId
    );
    const { sportsbookSelectionsState } = actionConditionsState;
    const {
        allStakeRequirementsOptions,
        betResultForm,
        betTypeForm,
        minBetOddsForm,
        stakeRequirements,
        minStakeCumulativeForm,
        minBetFrequencyForm,
        minStakeSingleForm,
        maxStakeSingleForm,
        stakeRequirementsForm,
        targetStakeForm,
    } = sportsbookSelectionsState;

    return (
        <>
            <section
                data-test='betting-types'
                className='flex flex-col gap-6'
            >
                <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Betting Types & Odds Criteria</h2>
                <div className='flex flex-col gap-4'>
                    <MultiOptionSelect
                        dataTestDropdown='dropdown-list'
                        dataTest='bet-type-dropdown'
                        errors={betTypeForm.errorsForView}
                        formInputState={betTypeForm}
                        label='Bet Type'
                        nodes={BET_TYPE_OPTIONS}
                        disabled={editionDisabled}
                    />
                    <Input
                        errors={minBetOddsForm.errorsForView}
                        formInputState={minBetOddsForm}
                        label='Min Bet Odds'
                        onBlur={() => formatNumericInput(minBetOddsForm)}
                        placeholder='Type'
                        disabled={editionDisabled}
                    />
                </div>
            </section>

            <section
                data-test='stake-criteria'
                className='flex flex-col gap-6'
            >
                <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>Stake Criteria</h2>
                <div className='flex flex-col gap-4'>
                    <Select
                        dataTest='stake-req-dropdown'
                        errors={stakeRequirementsForm.errorsForView}
                        formInputState={stakeRequirementsForm}
                        label='Stake Requirements'
                        placeholder='Select'
                        values={allStakeRequirementsOptions}
                        disabled={editionDisabled}
                    />
                    {stakeRequirements === 'cumulative' && (
                        <>
                            <Input
                                dataTest='target-stake'
                                description='Enter a value greater or equal to £0.10'
                                errors={targetStakeForm.errorsForView}
                                formInputState={targetStakeForm}
                                label='Target Stake'
                                onBlur={() => formatNumericInput(targetStakeForm)}
                                placeholder='Type'
                                disabled={editionDisabled}
                            />
                            {generalDetailsState.campaignTypeForm.value === 'settlement' ? (
                                <ResultsMultiSelect
                                    formInputState={betResultForm}
                                    disabled={editionDisabled}
                                />
                            ) : null}
                            <div className='grid grid-cols-2 gap-4'>
                                <Input
                                    dataTest='min-stake-input'
                                    description='Enter a value greater or equal to £0.10'
                                    errors={minStakeCumulativeForm.errorsForView}
                                    formInputState={minStakeCumulativeForm}
                                    label='Min Stake (Optional)'
                                    onBlur={() => formatNumericInput(minStakeCumulativeForm)}
                                    placeholder='Type'
                                    disabled={editionDisabled}
                                />
                                <Input
                                    dataTest='max-stake-input'
                                    description='Enter a number between 1 and 100'
                                    errors={minBetFrequencyForm.errorsForView}
                                    formInputState={minBetFrequencyForm}
                                    label='Min Bet Frequency (Optional)'
                                    placeholder='Type'
                                    disabled={editionDisabled}
                                />
                            </div>
                        </>
                    )}
                    {stakeRequirements === 'single' && (
                        <>
                            {generalDetailsState.campaignTypeForm.value === 'settlement' ? (
                                <ResultsMultiSelect
                                    formInputState={betResultForm}
                                    disabled={editionDisabled}
                                />
                            ) : null}
                            <div className='grid grid-cols-2 gap-4'>
                                <Input
                                    dataTest='min-stake-input'
                                    description='Enter a value greater or equal to £0.10'
                                    errors={minStakeSingleForm.errorsForView}
                                    formInputState={minStakeSingleForm}
                                    label='Min Stake'
                                    onBlur={() => formatNumericInput(minStakeSingleForm)}
                                    placeholder='Type'
                                    disabled={editionDisabled}
                                />
                                <Input
                                    dataTest='max-stake-input'
                                    description="Enter a value greater than 'Min Stake'"
                                    errors={maxStakeSingleForm.errorsForView}
                                    formInputState={maxStakeSingleForm}
                                    label='Max Stake (Optional)'
                                    onBlur={() => formatNumericInput(maxStakeSingleForm)}
                                    placeholder='Type'
                                    disabled={editionDisabled}
                                />
                            </div>
                        </>
                    )}
                </div>
            </section>

            <section
                data-test='sports-picker'
                className='flex flex-col gap-6'
            >
                <h2 className='m-0 text-[1rem] font-semibold leading-[1.75]'>
                    {'Eligible: Sport > Competition > Event > Market'}
                </h2>
                <SportsPicker
                    campaignId={campaignId}
                    disabled={editionDisabled}
                />
            </section>
        </>
    );
});
